import React from "react";
import { Button, ButtonProps } from "@chakra-ui/react";

// Wraps a Chakra Button so that when `onClick` returns a Promise, the button
// renders its loading spinner until the Promise settles. Plain (non-async)
// onClick handlers behave identically to the underlying Button.
export interface AsyncButtonProps extends Omit<ButtonProps, "onClick"> {
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<unknown>;
}

export const AsyncButton: React.FC<AsyncButtonProps> = ({
  onClick,
  isLoading,
  isDisabled,
  children,
  ...rest
}) => {
  const [pending, setPending] = React.useState(false);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick || pending) return;
    const result = onClick(event);
    if (result && typeof (result as Promise<unknown>).then === "function") {
      setPending(true);
      try {
        await result;
      } finally {
        if (mounted.current) setPending(false);
      }
    }
  };

  return (
    <Button
      {...rest}
      isLoading={isLoading || pending}
      isDisabled={isDisabled || pending}
      onClick={handleClick}
    >
      {/* Loading replaces this element, even if translation replaced its text. */}
      <span>{children}</span>
    </Button>
  );
};
